import { modalHide } from "app/App/actions";
import { FC, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Form,
    LoadingButton,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    RequestMessage,
    TextField,
} from "ui";
import { IAddTranslation } from "../interfaces";
import useRequest from "api/useRequest";
import {
    Box,
    Divider,
    Grid,
    Icon,
    IconButton,
    Typography,
} from "@mui/material";
import trans from "helpers/trans";
import { IGPFormRef } from "ui/Form/Form";
import { AxiosResponse } from "axios";
import {
    translate,
    translateCreate,
    translateImprove,
    translateUpdate,
} from "../actions";
import { isEmpty } from "lodash";

const AddTranslationModal: FC<IAddTranslation> = ({
    data,
    project,
    id,
    onNewSuccess,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const formRef = useRef<IGPFormRef | null>();

    const languagePermission = useSelector((state: any) => ({
        context: {
            read: state.me.permissions["translation.context.read"],
            update: state.me.permissions["translation.context.update"],
        },
        lang_en: {
            read: state.me.permissions["translation.lang_en.read"],
            update: state.me.permissions["translation.lang_en.update"],
        },
        lang_da: {
            read: state.me.permissions["translation.lang_da.read"],
            update: state.me.permissions["translation.lang_da.update"],
        },
        lang_de: {
            read: state.me.permissions["translation.lang_de.read"],
            update: state.me.permissions["translation.lang_de.update"],
        },
        lang_fi: {
            read: state.me.permissions["translation.lang_fi.read"],
            update: state.me.permissions["translation.lang_fi.update"],
        },
        lang_nb: {
            read: state.me.permissions["translation.lang_nb.read"],
            update: state.me.permissions["translation.lang_nb.update"],
        },
        lang_sv: {
            read: state.me.permissions["translation.lang_sv.read"],
            update: state.me.permissions["translation.lang_sv.update"],
        },
    }));

    const [improveText, setImproveText] = useState<Record<string, string[]>>(
        {},
    );
    const { errors, isLoading, message, request, status } = useRequest();
    const {
        errors: updateErrors,
        isLoading: updateIsLoading,
        message: updateMessage,
        request: updateRequest,
        status: updateStatus,
    } = useRequest();

    const { isLoading: translateIsLoading, request: translateRequest } =
        useRequest();
    const { isLoading: improveIsLoading, request: improveRequest } =
        useRequest();

    const handleClose = () => {
        dispatch(modalHide("AddTranslationModal"));
    };
    const handleTranslation = (reload: Function) => {
        if (formRef.current !== null) {
            const dataToSend = formRef?.current?.getData();
            if (id)
                updateRequest(
                    translateUpdate(
                        id,
                        project,
                        dataToSend,
                        (response: AxiosResponse) => {
                            if (response.status === 204) {
                                reload();
                            }
                        },
                    ),
                );
            else
                request(
                    translateCreate(
                        project,
                        dataToSend,
                        (response: AxiosResponse) => {
                            if (response.status < 300) {
                                reload();
                            }
                        },
                    ),
                );
        }
    };

    const handleSaveNewTranslation = () => {
        handleTranslation(() => {
            handleClose();
            onNewSuccess();
            if (formRef.current) {
                const data = formRef.current.getData();
                formRef.current.refreshFormData({
                    key: data.key.replace(/\.[^.]*$/, "."),
                    context: "",
                    lang_en: "",
                    lang_da: "",
                    lang_de: "",
                    lang_fi: "",
                    lang_nb: "",
                    lang_sv: "",
                });
            }
        });
    };

    const handleSaveTranslation = () => {
        handleTranslation(() => {
            handleClose();
            onSuccess();
        });
    };

    const handleTranslateClick = (lang: string) => {
        if (formRef === undefined) {
            alert("Something went wrong");
        }
        translateRequest(
            translate(
                id as number,
                project,
                [lang],
                {},
                (response: AxiosResponse) => {
                    if (response.status === 200) {
                        if (formRef.current) {
                            formRef.current.onChange([
                                {
                                    id: `lang_${lang}`,
                                    value: response.data[lang],
                                },
                            ]);
                        }
                    }
                },
            ),
        );
    };

    const handleImproveClick = (lang: string) => {
        if (formRef === undefined) {
            alert("Something went wrong");
        }

        improveRequest(
            translateImprove(
                lang,
                formRef?.current?.getData()[`lang_${lang}`] || "",
                {},
                (response: AxiosResponse) => {
                    if (response.status === 200) {
                        setImproveText((prev) => ({
                            ...prev,
                            [lang]: [response.data.text],
                        }));
                    }
                },
            ),
        );
    };

    const handleClickImproveAlternative =
        (lang: string, text: string) => () => {
            if (formRef.current) {
                formRef.current.onChange([
                    {
                        id: `lang_${lang}`,
                        value: text,
                    },
                ]);
            }
        };

    return (
        <Modal open maxWidth="md" fullWidth>
            <ModalTitle onClose={handleClose}>
                {trans("admin.translate.title")}
            </ModalTitle>
            <ModalContent>
                <RequestMessage
                    status={
                        updateStatus >= 400 || status >= 400
                            ? Math.max(updateStatus, status)
                            : 0
                    }
                    message={
                        updateMessage ? updateMessage : message ? message : ""
                    }
                    sx={{
                        mb: 1,
                    }}
                />
                <Form
                    ref={formRef}
                    data={data as any}
                    errors={
                        !isEmpty(errors)
                            ? errors
                            : !isEmpty(updateErrors)
                              ? updateErrors
                              : {}
                    }
                    fields={{
                        key: {},
                        context: {},
                        lang_en: {},
                        lang_da: {},
                        lang_de: {},
                        lang_fi: {},
                        lang_nb: {},
                        lang_sv: {},
                    }}
                    loading={isLoading || updateIsLoading}
                    onSubmit={() => {}}
                    unsaved={false}
                >
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                autoFocus={!id}
                                disabled={!!id}
                                id="key"
                                label={trans(
                                    "admin.translate.modal.addTranslationModal.key",
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12}>
                            {languagePermission.lang_en.read && (
                                <TextField
                                    id="lang_en"
                                    fullWidth
                                    label="English"
                                    disabled={
                                        !languagePermission.lang_en.update
                                    }
                                    InputProps={{
                                        endAdornment: languagePermission.lang_en
                                            .update && (
                                            <IconButton
                                                disabled={improveIsLoading}
                                                onClick={() =>
                                                    handleImproveClick("en")
                                                }
                                            >
                                                <Icon>spellcheck</Icon>
                                            </IconButton>
                                        ),
                                    }}
                                />
                            )}
                            {improveText["en"]?.length > 0 && (
                                <Box mt={1}>
                                    {improveText["en"].map((text, index) => (
                                        <Typography
                                            key={index}
                                            variant="body2"
                                            onClick={handleClickImproveAlternative(
                                                "en",
                                                text,
                                            )}
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                p: 1,
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "action.hover",
                                                },
                                            }}
                                        >
                                            {text}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                            {languagePermission.context.read && (
                                <TextField
                                    id="context"
                                    fullWidth
                                    label="Context"
                                    disabled={
                                        !languagePermission.context.update
                                    }
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Grid>
                    </Grid>
                    <Divider sx={{ mb: 2 }} />

                    {languagePermission.lang_nb.read && (
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12}>
                                <TextField
                                    disabled={
                                        !languagePermission.lang_nb.update
                                    }
                                    id="lang_nb"
                                    fullWidth
                                    label="Norwegian Bokmål"
                                    InputProps={{
                                        endAdornment: [
                                            // <IconButton
                                            //     key={"improve-nb"}
                                            //     disabled={translateIsLoading}
                                            //     onClick={() =>
                                            //         handleImproveClick("nb")
                                            //     }
                                            // >
                                            //     <Icon>spellcheck</Icon>
                                            // </IconButton>,
                                            id &&
                                                languagePermission.lang_nb
                                                    .update && (
                                                    <IconButton
                                                        key={"translate-nb"}
                                                        disabled={
                                                            translateIsLoading
                                                        }
                                                        onClick={() =>
                                                            handleTranslateClick(
                                                                "nb",
                                                            )
                                                        }
                                                    >
                                                        <Icon>translate</Icon>
                                                    </IconButton>
                                                ),
                                        ],
                                    }}
                                />
                                {improveText["nb"]?.length > 0 && (
                                    <Box mt={1}>
                                        {improveText["nb"].map(
                                            (text, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    onClick={handleClickImproveAlternative(
                                                        "nb",
                                                        text,
                                                    )}
                                                    sx={{
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        p: 1,
                                                        borderRadius: 1,
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "action.hover",
                                                        },
                                                    }}
                                                >
                                                    {text}
                                                </Typography>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {languagePermission.lang_da.read && (
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12}>
                                <TextField
                                    disabled={
                                        !languagePermission.lang_da.update
                                    }
                                    id="lang_da"
                                    fullWidth
                                    label="Danish"
                                    InputProps={{
                                        endAdornment: [
                                            // <IconButton
                                            //     key={"improve-da"}
                                            //     disabled={translateIsLoading}
                                            //     onClick={() =>
                                            //         handleImproveClick("da")
                                            //     }
                                            // >
                                            //     <Icon>spellcheck</Icon>
                                            // </IconButton>,
                                            id &&
                                                languagePermission.lang_da
                                                    .update && (
                                                    <IconButton
                                                        key={"translate-da"}
                                                        disabled={
                                                            translateIsLoading
                                                        }
                                                        onClick={() =>
                                                            handleTranslateClick(
                                                                "da",
                                                            )
                                                        }
                                                    >
                                                        <Icon>translate</Icon>
                                                    </IconButton>
                                                ),
                                        ],
                                    }}
                                />
                                {improveText["da"]?.length > 0 && (
                                    <Box mt={1}>
                                        {improveText["da"].map(
                                            (text, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    onClick={handleClickImproveAlternative(
                                                        "da",
                                                        text,
                                                    )}
                                                    sx={{
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        p: 1,
                                                        borderRadius: 1,
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "action.hover",
                                                        },
                                                    }}
                                                >
                                                    {text}
                                                </Typography>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {languagePermission.lang_de.read && (
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12}>
                                <TextField
                                    disabled={
                                        !languagePermission.lang_de.update
                                    }
                                    id="lang_de"
                                    fullWidth
                                    label="German"
                                    InputProps={{
                                        endAdornment: [
                                            // <IconButton
                                            //     key={"improve-de"}
                                            //     disabled={translateIsLoading}
                                            //     onClick={() =>
                                            //         handleImproveClick("de")
                                            //     }
                                            // >
                                            //     <Icon>spellcheck</Icon>
                                            // </IconButton>,
                                            id &&
                                                languagePermission.lang_de
                                                    .update && (
                                                    <IconButton
                                                        key={"translate-de"}
                                                        disabled={
                                                            translateIsLoading
                                                        }
                                                        onClick={() =>
                                                            handleTranslateClick(
                                                                "de",
                                                            )
                                                        }
                                                    >
                                                        <Icon>translate</Icon>
                                                    </IconButton>
                                                ),
                                        ],
                                    }}
                                />
                                {improveText["de"]?.length > 0 && (
                                    <Box mt={1}>
                                        {improveText["de"].map(
                                            (text, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    onClick={handleClickImproveAlternative(
                                                        "de",
                                                        text,
                                                    )}
                                                    sx={{
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        p: 1,
                                                        borderRadius: 1,
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "action.hover",
                                                        },
                                                    }}
                                                >
                                                    {text}
                                                </Typography>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {languagePermission.lang_fi.read && (
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12}>
                                <TextField
                                    disabled={
                                        !languagePermission.lang_fi.update
                                    }
                                    id="lang_fi"
                                    fullWidth
                                    label="Finnish"
                                    InputProps={{
                                        endAdornment: [
                                            // <IconButton
                                            //     key={"improve-fi"}
                                            //     disabled={translateIsLoading}
                                            //     onClick={() =>
                                            //         handleImproveClick("fi")
                                            //     }
                                            // >
                                            //     <Icon>spellcheck</Icon>
                                            // </IconButton>,
                                            id &&
                                                languagePermission.lang_fi
                                                    .update && (
                                                    <IconButton
                                                        key={"translate-fi"}
                                                        disabled={
                                                            translateIsLoading
                                                        }
                                                        onClick={() =>
                                                            handleTranslateClick(
                                                                "fi",
                                                            )
                                                        }
                                                    >
                                                        <Icon>translate</Icon>
                                                    </IconButton>
                                                ),
                                        ],
                                    }}
                                />
                                {improveText["fi"]?.length > 0 && (
                                    <Box mt={1}>
                                        {improveText["fi"].map(
                                            (text, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    onClick={handleClickImproveAlternative(
                                                        "fi",
                                                        text,
                                                    )}
                                                    sx={{
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        p: 1,
                                                        borderRadius: 1,
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "action.hover",
                                                        },
                                                    }}
                                                >
                                                    {text}
                                                </Typography>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {languagePermission.lang_sv.read && (
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12}>
                                <TextField
                                    disabled={
                                        !languagePermission.lang_sv.update
                                    }
                                    id="lang_sv"
                                    fullWidth
                                    label="Swedish"
                                    InputProps={{
                                        endAdornment: [
                                            // <IconButton
                                            //     key={"improve-sv"}
                                            //     disabled={translateIsLoading}
                                            //     onClick={() =>
                                            //         handleImproveClick("sv")
                                            //     }
                                            // >
                                            //     <Icon>spellcheck</Icon>
                                            // </IconButton>,
                                            id &&
                                                languagePermission.lang_sv
                                                    .update && (
                                                    <IconButton
                                                        key={"translate-sv"}
                                                        disabled={
                                                            translateIsLoading
                                                        }
                                                        onClick={() =>
                                                            handleTranslateClick(
                                                                "sv",
                                                            )
                                                        }
                                                    >
                                                        <Icon>translate</Icon>
                                                    </IconButton>
                                                ),
                                        ],
                                    }}
                                />
                                {improveText["sv"]?.length > 0 && (
                                    <Box mt={1}>
                                        {improveText["sv"].map(
                                            (text, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    onClick={handleClickImproveAlternative(
                                                        "sv",
                                                        text,
                                                    )}
                                                    sx={{
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        p: 1,
                                                        borderRadius: 1,
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "action.hover",
                                                        },
                                                    }}
                                                >
                                                    {text}
                                                </Typography>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Form>
            </ModalContent>
            <ModalActions>
                <Box
                    display="flex"
                    width="100%"
                    textAlign="right"
                    gap={1}
                    justifyContent="end"
                >
                    <Button
                        onClick={handleClose}
                        color="inherit"
                        variant="text"
                        sx={{ mr: 1 }}
                    >
                        {trans("global.button.cancel")}
                    </Button>
                    <LoadingButton
                        loading={isLoading || updateIsLoading}
                        variant="outlined"
                        color="secondary"
                        onClick={handleSaveNewTranslation}
                    >
                        {trans("global.button.saveNew")}
                    </LoadingButton>
                    <LoadingButton
                        loading={isLoading || updateIsLoading}
                        variant="outlined"
                        color="secondary"
                        onClick={handleSaveTranslation}
                    >
                        {trans("global.button.save")}
                    </LoadingButton>
                </Box>
            </ModalActions>
        </Modal>
    );
};

export default AddTranslationModal;
